import { useState } from 'react';
import {
  X,
  Copy,
  Check,
  MessageCircle,
  ShieldAlert,
  Sparkles,
  FileCheck2,
  DollarSign,
} from 'lucide-react';
import { Button } from '@/components/ui/button.js';
import {
  generateChannelDeliveryMessage,
  checkTosCompliance,
  DeliveryMessageContext,
} from '@/lib/platformFees.js';
import { cleanPhoneForWhatsApp } from '@/lib/clientUtils.js';

interface ChannelDeliveryModalProps {
  isOpen: boolean;
  onClose: () => void;
  project: any;
  milestones?: any[];
  clientPhone?: string | null;
  clientEmail?: string | null;
  defaultChannel?: 'fiverr' | 'upwork' | 'direct';
}

export function ChannelDeliveryModal({
  isOpen,
  onClose,
  project,
  milestones = [],
  clientPhone,
  clientEmail: _clientEmail,
  defaultChannel,
}: ChannelDeliveryModalProps) {
  if (!isOpen || !project) return null;

  const projectPlatform = (project.platform || 'direct').toLowerCase() as 'fiverr' | 'upwork' | 'direct';
  const initialChannel: 'fiverr' | 'upwork' | 'direct' = defaultChannel || (
    projectPlatform === 'fiverr' ? 'fiverr' : projectPlatform === 'upwork' ? 'upwork' : 'direct'
  );

  const [activeChannel, setActiveChannel] = useState<'fiverr' | 'upwork' | 'direct'>(initialChannel);
  const [copied, setCopied] = useState(false);

  const context: DeliveryMessageContext = {
    projectName: project.name,
    clientName: project.client_name || 'Client',
    orderId: project.platform_order_id,
    milestones: milestones.map((m: any) => ({
      title: m.title,
      status: m.status,
    })),
    portalUrl: project.portal_token ? `${window.location.origin}/#/portal?token=${project.portal_token}` : null,
    invoiceNumber: project.invoice_number || (project.invoices?.[0]?.invoice_number) || null,
    balanceDue: project.budget || project.balance_due || null,
    previewUrl: project.platform_url || null,
    revisionsRemaining: project.fiverr_revisions_remaining ?? 2,
  };

  const messageText = generateChannelDeliveryMessage(activeChannel, context);
  const tosResult = checkTosCompliance(messageText, activeChannel);

  const handleCopy = async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(messageText);
      } else {
        const ta = document.createElement('textarea');
        ta.value = messageText;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      console.error('Failed to copy', err);
    }
  };

  const waPhone = cleanPhoneForWhatsApp(clientPhone);
  const waUrl = waPhone
    ? `https://wa.me/${waPhone}?text=${encodeURIComponent(messageText)}`
    : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-150">
      <div className="bg-slate-900 border border-slate-700 w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="p-4 sm:p-5 border-b border-slate-800 flex items-center justify-between bg-slate-950/60">
          <div>
            <h3 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-teal-400" />
              Channel Delivery & Follow-Up Generator
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">
              1-click pre-formatted deliverables note for {project.name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Channel Selector Tabs */}
        <div className="flex border-b border-slate-800 bg-slate-950/40 p-2 gap-2 overflow-x-auto">
          <button
            type="button"
            onClick={() => { setActiveChannel('fiverr'); setCopied(false); }}
            className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold flex items-center justify-center gap-2 transition-all ${
              activeChannel === 'fiverr'
                ? 'bg-emerald-600/30 text-emerald-300 border border-emerald-500/50 shadow-sm'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
            }`}
          >
            <span className="w-2 h-2 rounded-full bg-emerald-400" />
            Fiverr Delivery
          </button>

          <button
            type="button"
            onClick={() => { setActiveChannel('upwork'); setCopied(false); }}
            className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold flex items-center justify-center gap-2 transition-all ${
              activeChannel === 'upwork'
                ? 'bg-green-600/30 text-green-300 border border-green-500/50 shadow-sm'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
            }`}
          >
            <span className="w-2 h-2 rounded-full bg-green-400" />
            Upwork Escrow
          </button>

          <button
            type="button"
            onClick={() => { setActiveChannel('direct'); setCopied(false); }}
            className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold flex items-center justify-center gap-2 transition-all ${
              activeChannel === 'direct'
                ? 'bg-blue-600/30 text-blue-300 border border-blue-500/50 shadow-sm'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
            }`}
          >
            <span className="w-2 h-2 rounded-full bg-blue-400" />
            Direct WhatsApp / Email
          </button>
        </div>

        {/* Content Body */}
        <div className="p-4 sm:p-6 overflow-y-auto space-y-4 flex-1">
          {/* Channel Guidance Notice */}
          {activeChannel === 'fiverr' && (
            <div className="p-3 rounded-xl border border-emerald-500/20 bg-emerald-950/20 text-xs text-emerald-300 flex items-start gap-2">
              <FileCheck2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
              <div>
                <strong className="font-semibold">Fiverr Delivery Rules:</strong> Encourages 5-star reviews ethically without begging, clearly explains how to review, and specifies revision terms to prevent disputes.
              </div>
            </div>
          )}

          {activeChannel === 'upwork' && (
            <div className="p-3 rounded-xl border border-green-500/20 bg-green-950/20 text-xs text-green-300 flex items-start gap-2">
              <DollarSign className="w-4 h-4 text-green-400 shrink-0 mt-0.5" />
              <div>
                <strong className="font-semibold">Upwork Escrow Submission:</strong> Formally lists completed deliverables, handover test notes, and prompts the client to click 'Approve & Release Payment'.
              </div>
            </div>
          )}

          {activeChannel === 'direct' && (
            <div className="p-3 rounded-xl border border-blue-500/20 bg-blue-950/20 text-xs text-blue-300 flex items-start gap-2">
              <MessageCircle className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
              <div>
                <strong className="font-semibold">Direct Client Notice:</strong> Includes secure Client Portal link for DocuSign-style digital acceptance and invoice balance reference.
              </div>
            </div>
          )}

          {/* TOS Compliance Warning if violations exist */}
          {!tosResult.isCompliant && (
            <div className="p-3 rounded-xl border border-rose-500/40 bg-rose-950/40 text-rose-200 text-xs space-y-1">
              <div className="font-bold flex items-center gap-1.5 text-rose-400">
                <ShieldAlert className="w-4 h-4" />
                TOS Warning: Off-platform contact info found in message!
              </div>
              <p className="text-slate-300">
                Ensure all contact info is cleared before posting to {activeChannel.toUpperCase()}.
              </p>
            </div>
          )}

          {/* Text Area Display */}
          <div className="relative">
            <textarea
              readOnly
              value={messageText}
              rows={12}
              className="w-full bg-slate-950 border border-slate-700 rounded-xl p-3.5 text-xs sm:text-sm font-mono text-slate-200 focus:outline-none focus:ring-1 focus:ring-teal-500 selection:bg-teal-500/30 leading-relaxed resize-none"
            />
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-4 border-t border-slate-800 bg-slate-950/80 flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs text-slate-400">
            {activeChannel === 'fiverr' && (project.platform_order_id ? `Order #${project.platform_order_id}` : 'Fiverr Platform')}
            {activeChannel === 'upwork' && (project.platform_order_id ? `Contract #${project.platform_order_id}` : 'Upwork Platform')}
            {activeChannel === 'direct' && 'Direct Client Account'}
          </div>

          <div className="flex items-center gap-2">
            {activeChannel === 'direct' && waUrl && (
              <a
                href={waUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-emerald-600 hover:bg-emerald-500 text-white transition-colors"
              >
                <MessageCircle className="w-3.5 h-3.5" />
                Send via WhatsApp
              </a>
            )}

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onClose}
              className="text-xs"
            >
              Close
            </Button>

            <Button
              type="button"
              size="sm"
              onClick={handleCopy}
              className="bg-teal-600 hover:bg-teal-500 text-white text-xs gap-1.5 shadow-md shadow-teal-900/30"
            >
              {copied ? <Check className="w-3.5 h-3.5 text-white" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? 'Copied to Clipboard!' : '1-Click Copy Delivery Note'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
